<template>
  <div>
    <v-card-text class="pb-0">
      <p class="display-1 text--primary">{{ $t("report.thgClosing.title") }}</p>
      <p class="mb-0 pb-4 autoHypen" v-html="$t('report.thgClosing.description')"></p>
    </v-card-text>
    <v-row class="mt-4">
      <v-col cols="12" md="5">
        <thg-checkout-vehicle-card></thg-checkout-vehicle-card>
        <thg-checkout-personal-data-card
          v-if="!isAuthenticated"
          :dialogCloseButtonFunction="dialogCloseButtonFunction"
          :hideTitle="hideTitle"
          :hideEdit="hideEdit"
        />
      </v-col>
      <v-col cols="12" md="7">
        <v-card class="mb-10" max-width="850" flat outlined>
          <v-card-title :style="partnerColor.gradientStyle" class="title pt-4" v-if="!hideTitle">
            <span :style="`color:white`"> {{ $t("report.thgCheckoutCard.title") }}</span>
            <v-spacer />
          </v-card-title>
          <thg-checkout-order-card></thg-checkout-order-card>
          <v-form ref="form" v-model="valid">
            <v-checkbox
              v-model="isConfirmed"
              :disabled="userExists"
              ref="reviewed"
              dense
              :rules="reportReviewedRules"
              :label="$t('report.thgClosing.confirmation')"
              class="mx-4"
              data-test-review-checkbox
            ></v-checkbox>

            <v-checkbox
              :rules="[(v) => !!v || $t('report.thgClosing.readPrivacy')]"
              v-model="isTermsAccepted"
              dense
              :disabled="userExists"
              :label="$t('report.thgClosing.confirmationPrivacy')"
              ref="privacy"
              class="mx-4"
              data-test-privacy-checkbox
            >
            </v-checkbox>
            <v-checkbox
              v-if="!isAuthenticated"
              :disabled="userExists"
              dense
              v-model="isMarketingContactAllowed"
              class="mx-4"
              data-test-marketing-checkbox
            >
              <template v-slot:label>
                <span>
                  {{ dataLabel }}

                  <v-dialog
                    v-model="hintDialogOpen"
                    :fullscreen="$vuetify.breakpoint.xsOnly"
                    max-width="560"
                    scrollable
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-if="dataProtectionHint"
                        icon
                        small
                        class="ml-1"
                        v-bind="attrs"
                        v-on="on"
                        @click.stop.prevent
                      >
                        <v-icon>mdi-help-box</v-icon>
                      </v-btn>
                    </template>
                    <v-card>
                      <v-card-title>
                        {{ $t("common.nouns.hint") }}
                        <v-spacer></v-spacer>
                        <v-btn icon @click="hintDialogOpen = false">
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </v-card-title>
                      <v-card-text>
                        <span v-html="dataProtectionHint" />
                      </v-card-text>
                    </v-card>
                  </v-dialog>
                </span>
              </template>
            </v-checkbox>
            <v-card-text v-if="hint"> {{ hint }}</v-card-text>
            <div class="px-4 mb-4">
              <v-btn
                v-if="!userExists && !hideSubmit"
                @click="submit"
                color="success"
                block
                x-large
                :disabled="!isDone"
                :loading="isLoading"
                data-test-submit-button
              >
                <v-icon left> mdi-send </v-icon>
                {{ $t("report.thgCheckoutCard.submit") }}
              </v-btn>
            </div>
          </v-form>
          <v-card-text v-if="userExists" class="red--text"> {{ $t(userExistsText) }}</v-card-text>
          <div class="px-4 mb-4">
            <v-btn
              v-if="userExists"
              @click="goToLogin"
              color="success"
              x-large
              block
              :loading="isLoading"
              data-test-submit-button
            >
              {{ $t("report.thgClosing.login") }}
            </v-btn>
          </div>
          <div class="text-center text-caption mb-4 mx-4">
            <v-icon small>mdi-shield-check</v-icon>{{ $t("report.trustBanner.privacy") }}
            <v-icon small>mdi-lock</v-icon> {{ $t("report.trustBanner.encryption") }}
            <v-icon small>mdi-check-decagram</v-icon>
            {{ $t("report.trustBanner.datacenter") }}
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import ThgCheckoutOrderCard from "@/components/thg/ThgCheckoutOrderCard.vue";
import ThgCheckoutPersonalDataCard from "@/components/thg/ThgCheckoutPersonalDataCard.vue";
import ThgCheckoutVehicleCard from "@/components/thg/ThgCheckoutVehicleCard.vue";
import LayoutReportStep from "@/layouts/LayoutReportStep.vue";
import { PartnerColor } from "@/lib/partnerColor";
import { requiredRule } from "@/lib/rules/requiredRule";
import { Type } from "@/lib/utility/type";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { ReportModule } from "@/store/modules/report.store";
import { ThgCreateModule } from "@/store/modules/thg.create.store";
import { Component, Prop, Vue } from "vue-property-decorator";
import { UserModule } from "@/store/modules/me-user.store";

@Component({
  components: {
    LayoutReportStep,
    ThgCheckoutPersonalDataCard,
    ThgCheckoutOrderCard,
    ThgCheckoutVehicleCard
  }
})
export default class ThgCheckoutCard extends Vue {
  @Prop()
  loading!: boolean;

  @Prop()
  dialogCloseButtonFunction?: Function;

  @Prop({ default: false })
  hideTitle?: boolean;

  @Prop({ default: "" })
  hint?: string;

  @Prop({ default: "" })
  dataProtectionHint?: string;

  @Prop({ default: false })
  dataProtectionLabel?: string;

  @Prop({ default: false })
  hideEdit?: boolean;

  @Prop({ default: false })
  hideSubmit?: boolean;

  @Prop({ default: false })
  userExists?: boolean;

  hintDialogOpen = false;

  get dataLabel() {
    if (!this.dataProtectionLabel) {
      return this.$t("report.thgClosing.marketing");
    }
    return this.dataProtectionLabel;
  }

  get isLoading() {
    return this.loading;
  }

  set isLoading(n: boolean) {
    this.$emit("update:loading", n);
  }

  partnerColor = new PartnerColor();

  mounted() {
    this.isConfirmed = false;
  }

  valid = false;

  userExistsText = "report.thgClosing.userAlreadyExists";

  get isDone() {
    return this.valid && this.isConfirmed;
  }

  get form(): Vue & { validate: () => boolean } {
    return this.$refs.form as Vue & { validate: () => boolean };
  }

  get reportReviewedRules() {
    const rules = [];

    rules.push(requiredRule());
    rules.push(this.thgRegistrationImageValidRule());
    rules.push(this.numberplateIsMandatory());

    //TODO: Make this ways smarter
    if (ReportModule.screenOder.includes(ReportScreenEnum.thgcontact) && !this.isAuthenticated) {
      rules.push(this.reportContactEmailValidRule());
      rules.push(this.reportUserValidRule());
    }

    if (ReportModule.screenOder.includes(ReportScreenEnum.thgpassword) && !this.isAuthenticated) {
      rules.push(this.passwordIsMandatory());
    }

    return rules;
  }

  get isAuthenticated() {
    return UserModule.isAuthenticated;
  }

  passwordIsMandatory(message = "report.thgClosing.passwordMandatory"): boolean | string {
    const passwordValid = Type.isPresent(ThgCreateModule.password) && !Type.isBlankString(ThgCreateModule.password);
    return passwordValid || String(this.$t(message));
  }

  numberplateIsMandatory(message = "report.thgClosing.numberplateMandatory"): boolean | string {
    const numberplateValid =
      Type.isPresent(ThgCreateModule.numberplate) && !Type.isBlankString(ThgCreateModule.numberplate);
    return numberplateValid || String(this.$t(message));
  }

  reportContactEmailValidRule(message = "report.summary.mailMandatory"): boolean | string {
    return (
      (Type.isPresent(ThgCreateModule.contact.email) && !Type.isBlankString(ThgCreateModule.contact.email)) ||
      String(this.$t(message))
    );
  }

  thgRegistrationImageValidRule(message = "report.thgClosing.registrationImagesMandatory"): boolean | string {
    return ThgCreateModule.registrationImageFront.length !== 0 || String(this.$t(message));
  }

  reportUserValidRule(message = "report.summary.nameMandatory"): boolean | string {
    return (
      (Type.isPresent(ThgCreateModule.user.firstName) &&
        Type.isPresent(ThgCreateModule.user.lastName) &&
        !Type.isBlankString(ThgCreateModule.user.firstName) &&
        !Type.isBlankString(ThgCreateModule.user.lastName)) ||
      String(this.$t(message))
    );
  }

  get isTermsAccepted(): boolean {
    return ThgCreateModule.isTermsAccepted;
  }

  set isTermsAccepted(bool: boolean) {
    ThgCreateModule.setIsTermsAccepted(bool);
  }

  get isConfirmed(): boolean {
    return ThgCreateModule.isConfirmed;
  }

  set isConfirmed(bool: boolean) {
    ThgCreateModule.setIsConfirmed(bool);
  }

  get isMarketingContactAllowed(): boolean {
    return ThgCreateModule.isMarketingOptIn;
  }

  set isMarketingContactAllowed(bool: boolean) {
    ThgCreateModule.setIsMarketingOptIn(bool);
  }

  goToLogin() {
    this.$router.push({ path: "/login" });
  }

  async submit() {
    this.$emit("submit");
  }
}
</script>

<style scoped>
.text {
  color: rgba(0, 0, 0, 0.6);
}
</style>
